<template>
  <b-tab title="Assessor" lazy>
    <!-- Tab title name -->
    <div slot="title">
      <span class="font-size-md active span__instructor-tab">Assessor</span>
      <div class="divider" />
    </div>
    <div class="d-flex justify-content-between mb-2">
      <div class="h5 font-weight-bold font-size-lg d-flex align-items-center">Assessor list</div>

      <div v-if="$currentUser.isOwnerOf(learningUnitId)">
        <b-button
          ref="button__add-instructor"
          variant="first"
          @click.prevent.stop="showModal(`ModalAddInstructor`)"
        >
          <span> <font-awesome-icon icon="plus" class="mr-2" />Add assessor </span>
        </b-button>

        <ModalAddInstructor
          ref="ModalAddInstructor"
          :learning-unit-id="learningUnitId"
          :member-user-ids="memberUserIds"
          :current-members="instructorList"
          role="lu_assessor"
          @onAddNewMember="onAddNewMember"
        />
      </div>
    </div>

    <div v-if="isLoading" class="d-flex justify-content-center">
      <b-spinner label="Loading..." variant="dark" />
    </div>

    <table v-else class="table mb-5">
      <thead>
        <tr>
          <th class="font-weight-bold">NAME</th>
          <th />
          <th class="font-weight-bold">ROLE</th>
          <th />
        </tr>
      </thead>
      <tbody
        v-for="(instructor, index) in instructorList"
        :key="index"
        :class="`py-2 instructor-${index}`"
        class="border-bottom"
      >
        <InstructorContent
          :deleteable="$currentUser.isOwnerOf(learningUnitId)"
          :instructor="instructor"
          @removeMember="onRemoveMember"
        />
      </tbody>
    </table>
  </b-tab>
</template>

<script>
import { channelMixin } from '@/mixins/channelMixin';
import { userInfoMixin } from '@/mixins/userInfoMixin';
import LearningUnitMember from '@/models/LearningUnitMember';
import UserRole from '@/models/UserRole';
import ModalAddInstructor from './components/ModalAddInstructor';
import InstructorContent from './components/InstructorContent';

export default {
  components: {
    ModalAddInstructor,
    InstructorContent
  },

  mixins: [channelMixin, userInfoMixin],

  props: {
    disabled: {
      type: [Number, Boolean],
      default: false
    }
  },

  data() {
    return {
      isLoading: false,
      instructorList: [],
      fields: ['name', { key: 'tag', label: 'ROLE' }, { key: 'isOwner', label: '' }]
    };
  },

  computed: {
    memberUserIds() {
      return this.instructorList.map(({ userId = null } = {}) => userId).filter(Boolean);
    },

    learningUnitId() {
      return parseInt(this.$route.params.learningUnitId);
    }
  },

  watch: {
    '$route.path': {
      immediate: true,
      handler: 'onRouteChanged'
    }
  },

  methods: {
    async onRouteChanged() {
      await this.fetch();
    },

    async onAddNewMember(newInstructor) {
      if (newInstructor && newInstructor.id) {
        try {
          this.$_channelMixin_addUserToLearningUnitChannels(this.learningUnitId, newInstructor.id);
        } catch (error) {
          console.error(error);
        }
      }

      await this.fetch();
    },

    async onRemoveMember(removedMember) {
      if (removedMember && removedMember.userId) {
        try {
          this.$_channelMixin_removeUserFromLearningUnitChannels(
            this.learningUnitId,
            removedMember.userId
          );
        } catch (error) {
          console.error(error);
        }
      }

      await this.fetch();
    },

    showModal(modalType) {
      this.$refs[modalType].show(modalType);
    },

    async fetch() {
      this.isNotFound = false;
      this.isLoading = true;

      try {
        let promise = await LearningUnitMember.api().fetch({
          learning_unit: this.learningUnitId
        });

        const { data = [] } = promise.response;

        this.isNotFound = data.length === 0;

        this.instructorList = data.map((record) => {
          return {
            id: record.id,
            firstname: record.user.firstname,
            lastname: record.user.lastname,
            email: record.user.email,
            tag: UserRole.find(record.user_role).role,
            userId: record.user ? record.user.id : null,
            profile_picture: record.user ? record.user.profile_picture : null
          };
        });
      } catch (error) {
        console.error(error);
        this.$toast.error('Cannot load assessors, please try again');
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.table th,
.table td {
  padding: 0.75 rem;
  vertical-align: top;
  border: none !important;
  background: transparent !important;
}
</style>
